/**
 * **Card 3 — Documents** of the booking-flow editor (C25, FR-J11, FR-F3;
 * [ADR-0053](../../../../../../docs/adr/0053-a-booking-may-ask-for-documents-and-a-visitor-may-upload-them.md)
 * §1; Wave 27C, T-5.77). What a booking asks the visitor to send — an ID card,
 * an address proof — **up to five**, each a label, a line of help and whether
 * it is required, added, moved and removed as the questions are.
 *
 * The card sits between *what they can book* and *payment* because that is
 * where the visitor meets it: the booking page's documents step comes after
 * *choose* and before *pay*, and is drawn only when the flow asks for one
 * (ADR-0053 §2). A flow that asks for none is the page it always was.
 *
 * Each document has a **key** the firm never sees — `document_<n>`, kept when
 * the list is reordered — which is the slot its files are held and filed
 * under (`booking_slot`). `payment_proof` is the bank-QR screenshot's and no
 * document takes it.
 */

import { Button, Card, CardBody, Checkbox, Input } from '@fiveox/ui'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'

import { type FlowDocument, type FlowVocabulary, MAX_DOCUMENTS, PROOF_SLOT } from '../../../lib/api/booking'
import { CardHead } from './cardParts'

/** The service's limits (`DOCUMENT_LABEL_MAX`, `DOCUMENT_HINT_MAX`), for a
 *  vocabulary from before C25. */
export const DOCUMENT_LIMITS = { label: 60, hint: 160 }

/** A fresh key for a new document: `document_1`, `document_2`, … unused — the
 *  service's own default shape. Never the payment screenshot's slot. */
export function nextDocumentKey(documents: Pick<FlowDocument, 'key'>[]): string {
  const taken = new Set(documents.map((d) => d.key))
  for (let n = documents.length + 1; ; n += 1) {
    const key = `document_${n}`
    if (!taken.has(key) && key !== PROOF_SLOT) return key
  }
}

/** The documents as Save sends them: label and hint trimmed. */
export function cleanDocuments(documents: FlowDocument[]): FlowDocument[] {
  return documents.map((d) => ({ key: d.key, label: d.label.trim(), hint: (d.hint ?? '').trim(), required: Boolean(d.required) }))
}

/** What stops Save on the documents card — the service's rule, said first. */
export function documentProblems(documents: FlowDocument[], max = MAX_DOCUMENTS): string[] {
  const out: string[] = []
  if (documents.length > max) out.push(`A flow asks for at most ${max} documents.`)
  documents.forEach((d, index) => {
    if (!d.label.trim()) out.push(`Document ${index + 1} needs a label.`)
  })
  return out
}

export function DocumentsCard({
  n,
  documents,
  vocab,
  editable,
  onChange,
}: {
  n: number
  documents: FlowDocument[]
  vocab: FlowVocabulary
  editable: boolean
  onChange: (documents: FlowDocument[]) => void
}) {
  const max = vocab.max_documents ?? MAX_DOCUMENTS
  const limits = vocab.document_limits ?? DOCUMENT_LIMITS
  const full = documents.length >= max
  const setAt = (index: number, next: FlowDocument) => onChange(documents.map((d, i) => (i === index ? next : d)))
  const move = (index: number, by: -1 | 1) => {
    const to = index + by
    if (to < 0 || to >= documents.length) return
    const next = [...documents]
    const [moved] = next.splice(index, 1)
    next.splice(to, 0, moved as FlowDocument)
    onChange(next)
  }

  return (
    <Card data-card="documents">
      <CardBody className="space-y-4">
        <CardHead
          n={n}
          title="Documents"
          note="Files they send with the booking — an ID card, an address proof. Asked after they choose, before they pay."
        />
        <div className="space-y-3" data-documents="">
          <div className="flex items-center justify-between gap-2">
            <p className="text-foreground text-sm font-medium">What they send</p>
            <span className="text-muted-foreground text-xs">
              {documents.length} of {max}
            </span>
          </div>
          {documents.length === 0 ? (
            <p className="text-muted-foreground text-xs" data-documents-none="">
              None — the booking page asks for no files. Add up to {max}.
            </p>
          ) : (
            <p className="text-muted-foreground text-xs">
              A photo or a PDF, up to 10 MB, two a document. They can remove a file and send another until they
              finish. What they send is filed in the client’s documents, and is not in the client portal until you
              share it.
            </p>
          )}
          {documents.map((document, index) => (
            <div key={document.key} className="border-border space-y-3 rounded-lg border p-3" data-document={index}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground text-xs font-medium">Document {index + 1}</span>
                {editable && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Move document ${index + 1} up`}
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                    >
                      <ArrowUp size={14} aria-hidden />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Move document ${index + 1} down`}
                      disabled={index === documents.length - 1}
                      onClick={() => move(index, 1)}
                    >
                      <ArrowDown size={14} aria-hidden />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove document ${index + 1}`}
                      onClick={() => onChange(documents.filter((_, i) => i !== index))}
                    >
                      <Trash2 size={14} aria-hidden />
                    </Button>
                  </div>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  label="What to send"
                  maxLength={limits.label}
                  value={document.label}
                  placeholder="ID card (Aadhaar or PAN)"
                  disabled={!editable}
                  data-document-label={index}
                  onChange={(e) => setAt(index, { ...document, label: e.target.value })}
                />
                <Input
                  label="A line of help (optional)"
                  maxLength={limits.hint}
                  value={document.hint}
                  placeholder="Both sides, clearly readable"
                  disabled={!editable}
                  data-document-hint={index}
                  onChange={(e) => setAt(index, { ...document, hint: e.target.value })}
                />
              </div>
              <Checkbox
                label="They must send it"
                hint={document.required ? undefined : 'Optional: they can finish without it.'}
                checked={document.required}
                disabled={!editable}
                data-document-required={index}
                onChange={(e) => setAt(index, { ...document, required: e.target.checked })}
              />
            </div>
          ))}
          {editable && (
            <Button
              variant="secondary"
              size="sm"
              data-add-document=""
              disabled={full}
              title={full ? `A flow asks for at most ${max} documents.` : undefined}
              onClick={() =>
                onChange([...documents, { key: nextDocumentKey(documents), label: '', hint: '', required: false }])
              }
            >
              <Plus size={14} aria-hidden /> Add a document
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  )
}
